import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Blocks } from 'react-loader-spinner';
import axiosService from '../../helpers/axios';
import { variables } from '../../Variables.js';
import MyPage from '../../MyPage.js';
import SubTabsWarehouses from '../../SubTabsWarehouses.js';
import PoDelayMonths from './PoDelayMonths.jsx';
import { isSuperAdmin } from './../../helpers/user.js';
import { useSatellite } from '../../context/SatelliteContext';
import { getSatelliteApiParam, applySatelliteQueryToUrl, satellitePayloadValue, displaySatellite } from '../../helpers/satellite';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MONTHS = [
  'july', 'august', 'september', 'october', 'november', 'december',
  'january', 'february', 'march', 'april', 'may', 'june',
];

const MONTH_LABELS = {
  july: 'Jul', august: 'Aug', september: 'Sep', october: 'Oct',
  november: 'Nov', december: 'Dec', january: 'Jan', february: 'Feb',
  march: 'Mar', april: 'Apr', may: 'May', june: 'Jun',
};

const EMPTY_PURCHASE_FORM = {
  purchaseid: 0,
  warehousename: '',
  fyname: '',
  purchasetypename: 'CONTAINER',
  sapcode: '',
  satellite: '',
  itempurchasedname: '',
  comment: '',
  ...Object.fromEntries(MONTHS.map((m) => [m, ''])),
  ...Object.fromEntries(MONTHS.map((m) => [`povalidated${m}`, false])),
};

const PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const checkMonth = (monthInputs, month) =>
  monthInputs?.[month] !== '' ? monthInputs[month] ?? '' : '';

const checkMonthPoValidated = (monthCheckbox, month) =>
  monthCheckbox?.[month] ?? false;

/** Format month value: whole numbers as "25", decimals as "25.47" */
function formatMonthValue(value) {
  if (value == null || value === '') return 'N/A';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return Number.isInteger(n) ? String(Math.round(n)) : n.toFixed(2).replace(/\.?0+$/, '');
}

/** Format a monetary value with thousands separators and 2 decimals. */
function formatMoneyValue(value) {
  if (value == null || value === '') return 'N/A';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getCurrentFiscalYearAndMonth() {
  const today = new Date();
  const month = today.getMonth() + 1;
  const fiscalYear = month <= 6 ? today.getFullYear() : today.getFullYear() + 1;
  return { fiscalYear, fiscalMonth: month };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Actions cell renderer for Purchases grid */
function ActionsCellRenderer(params) {
  const { onEdit, onDelete } = params.context || {};
  const data = params.data;
  if (!data) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', alignItems: 'center' }}>
      <button
        type="button"
        disabled={!isSuperAdmin()}
        className="btn btn-light btn-sm p-0"
        data-bs-toggle="modal"
        data-bs-target="#editModal"
        onClick={() => onEdit?.(data)}
        title="Edit"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
          <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
          <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
        </svg>
      </button>
      <button
        type="button"
        disabled={!isSuperAdmin()}
        className="btn btn-light btn-sm p-0"
        onClick={() => onDelete?.(data.purchaseid)}
        title="Delete"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash-fill" viewBox="0 0 16 16">
          <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
        </svg>
      </button>
    </div>
  );
}

/** Generic loading spinner block */
function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '150vh' }}>
      <Blocks visible height="100" width="100" ariaLabel="blocks-loading" wrapperClass="blocks-wrapper" />
      <div style={{ marginLeft: '10px' }}>Loading...</div>
    </div>
  );
}

/** Modal wrapper */
function Modal({ id, title, children }) {
  return (
    <div className="modal fade" id={id} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" style={{ fontSize: '12px' }}>{title}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** A labelled form select */
function FormSelect({ label, value, onChange, options, disabled }) {
  return (
    <div className="input-group mb-3">
      <span className="input-group-text" style={{ fontSize: '12px' }}>{label}</span>
      <select className="form-select" value={value} onChange={onChange} disabled={disabled} style={{ fontSize: '12px' }}>
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

/** A labelled readonly text input */
function FormReadOnly({ label, value }) {
  return (
    <div className="input-group mb-3">
      <span className="input-group-text" style={{ fontSize: '12px' }}>{label}</span>
      <input type="text" className="form-control" value={value ?? ''} readOnly style={{ fontSize: '12px' }} />
    </div>
  );
}

/** A labelled text input */
function FormInput({ label, value, onChange, disabled }) {
  return (
    <div className="input-group mb-3">
      <span className="input-group-text" style={{ fontSize: '12px' }}>{label}</span>
      <input type="text" className="form-control" value={value ?? ''} onChange={onChange} disabled={disabled} style={{ fontSize: '12px' }} />
    </div>
  );
}

/** LEO / GEO satellite radio buttons */
function SatelliteCheckboxes({ value, onChange }) {
  const str = String(value ?? '').toUpperCase().trim();

  return (
    <div className="input-group mb-3">
      <span className="input-group-text" style={{ fontSize: '12px' }}>Satellite</span>
      <div className="form-control d-flex gap-4 align-items-center" style={{ fontSize: '12px' }}>
        {['LEO', 'GEO'].map((opt) => (
          <label key={opt} style={{ cursor: 'pointer', marginBottom: 0 }}>
            <input
              type="radio"
              name="satellite"
              value={opt}
              checked={str === opt}
              onChange={() => onChange(opt)}
              style={{ marginRight: 4 }}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function WarehousesStockProcurement() {
  const { satellite: globalSatellite } = useSatellite();

  // --- Lookup data ---
  const [fiscalYears, setFiscalYears] = useState([]);
  const [warehouseNames, setWarehouseNames] = useState([]);
  const [purchasetypeNames, setPurchasetypeNames] = useState([]);
  const [sapcodeNames, setSapcodeNames] = useState([]);
  const [sapcodeToDescription, setSapcodeToDescription] = useState({});
  const [containerdescriptions, setContainerdescriptions] = useState([]);
  const [containerToPoDelay, setContainerToPoDelay] = useState({});
  const [palletPoDelay, setPalletPoDelay] = useState(0);
  const [fynameToId, setFynameToId] = useState({});
  const [warehouseNameToId, setWarehouseNameToId] = useState({});

  // --- Purchases table ---
  const [purchasesAll, setPurchasesAll] = useState([]);
  const [loadingPurchase, setLoadingPurchase] = useState(true);
  const [isDownloadingPurchases, setIsDownloadingPurchases] = useState(false);

  // --- Stock procurement table ---
  const [stockprocurement, setStockprocurement] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [stockPage, setStockPage] = useState(1);
  const [stockTotalPages, setStockTotalPages] = useState(1);
  const [stockNextUrl, setStockNextUrl] = useState(null);
  const [stockPrevUrl, setStockPrevUrl] = useState(null);

  // --- Stock filters (for server-side search) ---
  const [stockFilters, setStockFilters] = useState({
    FiscalYearOFilter: '',
    WarehouseNameOFilter: '',
    ProductIdOFilter: '',
    ProductDescriptionOFilter: '',
    ItemTypeNameOFilter: '',
  });

  // --- Modal / form state ---
  const [modalTitle, setModalTitle] = useState('');
  const [form, setForm] = useState(EMPTY_PURCHASE_FORM);
  const [isEditing, setIsEditing] = useState(false);
  const [poDelayValue, setPoDelayValue] = useState(0);
  const [monthInputs, setMonthInputs] = useState({});
  const [monthCheckbox, setMonthCheckbox] = useState({});
  const [refresh, setRefresh] = useState(0);
  const [addPurchaseMode, setAddPurchaseMode] = useState('container');

  // ---------------------------------------------------------------------------
  // Data fetching helpers
  // ---------------------------------------------------------------------------
  const fetchPurchases = useCallback(async () => {
    setLoadingPurchase(true);
    try {
      const params = {};
      const sat = getSatelliteApiParam(globalSatellite);
      if (sat) params.satellite = sat;
      const { data } = await axiosService.get('/purchases/', { params });

      if (Array.isArray(data)) {
        setPurchasesAll(data);
        return;
      }

      let allResults = data.results ?? [];
      let nextUrl = data.next;
      while (nextUrl) {
        const relativeUrl = nextUrl.replace(variables.API_URL + 'api', '');
        const { data: nextData } = await axiosService.get(relativeUrl);
        allResults = [...allResults, ...(nextData.results ?? [])];
        nextUrl = nextData.next ?? null;
      }
      setPurchasesAll(allResults);
    } catch (e) {
      console.error('Error fetching purchases:', e);
    } finally {
      setLoadingPurchase(false);
    }
  }, [globalSatellite]);

  const downloadPurchases = useCallback(async () => {
    setIsDownloadingPurchases(true);
    try {
      const params = new URLSearchParams();
      const sat = getSatelliteApiParam(globalSatellite);
      if (sat) params.set('satellite', sat);
      const qs = params.toString();
      const response = await axiosService.get(
        `/purchases/download_csv/${qs ? `?${qs}` : ''}`,
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `purchases_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Error downloading purchases CSV:', e);
      alert('Failed to download purchases CSV file. Please try again.');
    } finally {
      setIsDownloadingPurchases(false);
    }
  }, [globalSatellite]);

  const fetchStockProcurement = useCallback(async (url = null) => {
    setLoadingStock(true);
    try {
      const raw = url ? url.replace(variables.API_URL + 'api', '') : '/stockprocurement/';
      const endpoint = applySatelliteQueryToUrl(raw, globalSatellite);
      const { data } = await axiosService.get(endpoint);
      const results = data.results ?? [];
      setStockprocurement(results);
      setStockTotalPages(Math.ceil((data.count ?? results.length) / PAGE_SIZE));
      setStockNextUrl(data.next ?? null);
      setStockPrevUrl(data.previous ?? null);
    } catch (e) {
      console.error('Error fetching stock procurement:', e);
    } finally {
      setLoadingStock(false);
    }
  }, [globalSatellite]);

  const fetchLookups = useCallback(async () => {
    try {
      const [yearsRes, warehousesRes, typesRes, productsRes, containersRes, palletRes] = await Promise.all([
        axiosService.get('/years/'),
        axiosService.get('/warehouses/'),
        axiosService.get('/purchasetypes/'),
        axiosService.get('/defaultproducts/'),
        axiosService.get('/containers/'),
        axiosService.get('/palletpodelay/'),
      ]);

      const toArray = (d) => (Array.isArray(d) ? d : (d?.results ?? []));

      const activeYears = toArray(yearsRes.data).filter((y) => y.isactive);
      setFiscalYears(activeYears.map((y) => y.fyname));
      setFynameToId(Object.fromEntries(activeYears.map((y) => [y.fyname, y.yearid])));

      const activeWarehouses = toArray(warehousesRes.data).filter((w) => w.isactive);
      setWarehouseNames(activeWarehouses.map((w) => w.warehousename));
      setWarehouseNameToId(Object.fromEntries(activeWarehouses.map((w) => [w.warehousename, w.warehouseid])));
      setPurchasetypeNames(toArray(typesRes.data).map((t) => t.purchasetypename));

      const activeProducts = toArray(productsRes.data).filter((p) => p.isactive);
      setSapcodeNames(activeProducts.map((p) => p.sapcode));
      setSapcodeToDescription(Object.fromEntries(activeProducts.map((p) => [p.sapcode, p.productdescription])));

      const activeContainers = toArray(containersRes.data).filter((c) => c.isactive);
      setContainerdescriptions(activeContainers.map((c) => c.containerdescription));
      setContainerToPoDelay(Object.fromEntries(activeContainers.map((c) => [c.containerdescription, c.podelay])));

      const pallets = toArray(palletRes.data);
      if (pallets.length > 0) {
        setPalletPoDelay(parseFloat(pallets[0].palletpodelay) || 0);
      }
    } catch (e) {
      console.error('Error fetching lookup data:', e);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
    fetchPurchases();
    fetchStockProcurement();
  }, [fetchLookups, fetchPurchases, fetchStockProcurement]);

  useEffect(() => {
    fetchPurchases();
    fetchStockProcurement();
  }, [globalSatellite, fetchPurchases, fetchStockProcurement]);

  // ---------------------------------------------------------------------------
  // Stock procurement search (server-side)
  // ---------------------------------------------------------------------------
  const searchStockProcurement = async () => {
    const params = new URLSearchParams();
    if (stockFilters.FiscalYearOFilter) params.append('year', stockFilters.FiscalYearOFilter);
    if (stockFilters.WarehouseNameOFilter) params.append('warehouse', stockFilters.WarehouseNameOFilter);
    if (stockFilters.ProductIdOFilter) params.append('sapcode', stockFilters.ProductIdOFilter);
    if (stockFilters.ProductDescriptionOFilter) params.append('product', stockFilters.ProductDescriptionOFilter);
    if (stockFilters.ItemTypeNameOFilter) params.append('itemtype', stockFilters.ItemTypeNameOFilter);
    const sat = getSatelliteApiParam(globalSatellite);
    if (sat) params.append('satellite', sat);
    setStockPage(1);
    await fetchStockProcurement(`${variables.API_URL}api/stockprocurement/?${params.toString()}`);
  };

  const clearStockFilters = () => {
    setStockFilters({ FiscalYearOFilter: '', WarehouseNameOFilter: '', ProductIdOFilter: '', ProductDescriptionOFilter: '', ItemTypeNameOFilter: '' });
    fetchStockProcurement();
  };

  const setStockFilter = (key) => (e) =>
    setStockFilters((prev) => ({ ...prev, [key]: e.target.value }));

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const handleNextPage = () => {
    if (stockNextUrl) { setStockPage((p) => p + 1); fetchStockProcurement(stockNextUrl); }
  };
  const handlePrevPage = () => {
    if (stockPrevUrl) { setStockPage((p) => p - 1); fetchStockProcurement(stockPrevUrl); }
  };

  // ---------------------------------------------------------------------------
  // Modal helpers
  // ---------------------------------------------------------------------------
  const openAddModal = () => {
    setForm(EMPTY_PURCHASE_FORM);
    setPoDelayValue(0);
    setMonthInputs({});
    setMonthCheckbox({});
    setIsEditing(false);
    setModalTitle('Add Purchase');
    setAddPurchaseMode('container');
  };

  // Default container on Add Purchase (once containers are loaded)
  useEffect(() => {
    const isAdd = modalTitle === 'Add Purchase' && form.purchaseid === 0;
    if (!isAdd) return;
    if (form.purchasetypename !== 'CONTAINER') return;
    if (form.itempurchasedname) return;
    if (containerdescriptions.length === 0) return;

    const first = containerdescriptions[0];
    setForm((f) => ({ ...f, itempurchasedname: first }));
    setPoDelayValue(containerToPoDelay[first] ?? 0);
  }, [
    modalTitle,
    form.purchaseid,
    form.purchasetypename,
    form.itempurchasedname,
    containerdescriptions,
    containerToPoDelay,
  ]);

  const openEditModal = (dep) => {
    const sanitized = Object.fromEntries(
      MONTHS.map((m) => [m, dep[m] ?? ''])
    );
    const poValidated = Object.fromEntries(
      MONTHS.map((m) => [`povalidated${m}`, dep[`povalidated${m}`] ?? false])
    );
    setForm({
      purchaseid: dep.purchaseid,
      warehousename: dep.warehouse?.warehousename ?? '',
      fyname: dep.year?.fyname ?? '',
      purchasetypename: dep.purchasetype?.purchasetypename ?? '',
      sapcode: dep.sapcode,
      satellite: dep.satellite ?? dep.product?.satellite ?? '',
      itempurchasedname: dep.itempurchasedname,
      comment: dep.comment ?? '',
      ...sanitized,
      ...poValidated,
    });
    const cDelay = containerToPoDelay[dep.itempurchasedname] ?? 0;
    setPoDelayValue(Number(cDelay) || palletPoDelay);
    setMonthInputs({});
    setMonthCheckbox({});
    setRefresh((r) => r + 1);
    setIsEditing(true);
    setModalTitle('Edit Purchase');
  };

  // ---------------------------------------------------------------------------
  // CRUD operations
  // ---------------------------------------------------------------------------
  const validateForm = () => {
    if (!form.warehousename) { alert('Warehouse is required'); return false; }
    if (!form.fyname) { alert('Year is required'); return false; }
    if (!form.purchasetypename) { alert('Purchasetype is required'); return false; }
    if (!form.itempurchasedname && !form.sapcode) { alert('Item (Container/Pallet or SAP Code) is required'); return false; }
    return true;
  };

  const buildMonthPayload = () => ({
    ...Object.fromEntries(
      MONTHS.map((m) => {
        const label = m.charAt(0).toUpperCase() + m.slice(1);
        return [m, checkMonth(monthInputs, label)];
      })
    ),
    ...Object.fromEntries(
      MONTHS.map((m) => {
        const label = m.charAt(0).toUpperCase() + m.slice(1);
        return [`povalidated${m}`, checkMonthPoValidated(monthCheckbox, label)];
      })
    ),
  });

  const createPurchase = async () => {
    if (!validateForm()) return;
    try {
      await axiosService.post('/purchasescreate/', {
        yearid: fynameToId[form.fyname],
        warehouseid: warehouseNameToId[form.warehousename],
        purchasetypename: form.purchasetypename,
        itempurchasedname: form.itempurchasedname,
        sapcode: form.sapcode,
        satellite: satellitePayloadValue(form.satellite),
        comment: form.comment,
        ...buildMonthPayload(),
      });
      alert('Purchase created');
      fetchPurchases();
      window.location.reload();
    } catch {
      alert('Failed to create purchase');
    }
  };

  const updatePurchase = async () => {
    if (!validateForm()) return;
    try {
      await axiosService.patch(`/purchases/${form.purchaseid}/`, {
        yearid: fynameToId[form.fyname],
        warehouseid: warehouseNameToId[form.warehousename],
        purchasetypename: form.purchasetypename,
        itempurchasedname: form.itempurchasedname,
        satellite: satellitePayloadValue(form.satellite),
        comment: form.comment,
        ...buildMonthPayload(),
      });
      alert('Update successful');
      fetchPurchases();
      window.location.reload();
    } catch {
      alert('Failed to update');
      window.location.reload();
    }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axiosService.delete(`/purchases/${id}/`);
      alert('Delete successful');
      fetchPurchases();
      window.location.reload();
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values for the form
  // ---------------------------------------------------------------------------
  const containerPoDelay = containerToPoDelay[form.itempurchasedname] ?? '';
  const isPallet = form.purchasetypename === 'PALLET';
  const isContainer = form.purchasetypename === 'CONTAINER';

  const purchasesColumnDefs = useMemo(() => [
    { field: 'purchaseid', headerName: 'Purchase Id', filter: 'agNumberColumnFilter', width: 110 },
    { field: 'po', headerName: 'PO', width: 130 },
    { headerName: 'Fiscal Year', valueGetter: (p) => p.data?.year?.fyname ?? 'N/A', width: 110 },
    { headerName: 'Warehouse', valueGetter: (p) => p.data?.warehouse?.warehousename ?? 'N/A', width: 140 },
    { headerName: 'Purchase Type', valueGetter: (p) => p.data?.purchasetype?.purchasetypename ?? 'N/A', width: 120 },
    { field: 'sapcode', headerName: 'ContainerID / Product SAP Code', width: 200 },
    {
      headerName: 'Satellite',
      valueGetter: (p) => displaySatellite(p.data?.satellite ?? p.data?.product?.satellite, { csv: true }),
      width: 110,
    },
    { field: 'itempurchasedname', headerName: 'Itempurchased', width: 140 },
    { field: 'net_order_price', headerName: 'Net Order Price', width: 130, filter: 'agNumberColumnFilter', valueFormatter: (p) => formatMoneyValue(p.value) },
    { field: 'total_value', headerName: 'Total Value', width: 120, filter: 'agNumberColumnFilter', valueFormatter: (p) => formatMoneyValue(p.value) },
    { field: 'currency', headerName: 'Currency', width: 100 },
    { field: 'comment', headerName: 'Comments', width: 120 },
    ...MONTHS.map((m) => ({
      field: m,
      headerName: MONTH_LABELS[m],
      width: 75,
      valueFormatter: (p) => formatMonthValue(p.value),
    })),
    {
      headerName: 'Options',
      width: 100,
      cellRenderer: ActionsCellRenderer,
      filter: false,
      sortable: false,
      pinned: 'right',
    },
  ], []);

  const stockColumnDefs = useMemo(() => [
    { field: 'fyname_o', headerName: 'Fiscal Year', width: 110 },
    { field: 'warehousename_o', headerName: 'Warehouse', width: 140 },
    { field: 'sapcode_o', headerName: 'Product SAP Code / SKU', width: 160 },
    { field: 'satellite', headerName: 'Satellite', width: 110, valueFormatter: (p) => displaySatellite(p.value, { csv: true }) },
    { field: 'productdescription_o', headerName: 'Product Description', width: 180 },
    { field: 'itemtypename_o', headerName: 'Item Type', width: 120 },
    { field: 'july_o', headerName: 'Jul', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'august_o', headerName: 'Aug', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'september_o', headerName: 'Sep', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'october_o', headerName: 'Oct', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'november_o', headerName: 'Nov', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'december_o', headerName: 'Dec', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'january_o', headerName: 'Jan', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'february_o', headerName: 'Feb', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'march_o', headerName: 'Mar', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'april_o', headerName: 'Apr', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'may_o', headerName: 'May', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
    { field: 'june_o', headerName: 'Jun', width: 80, valueFormatter: (p) => formatMonthValue(p.value) },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: true,
    minWidth: 70,
  }), []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsWarehouses />

      {/* ════════════════════════════════════════════════════════════
          Purchases table
      ════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', fontWeight: 'bold', fontSize: '14px' }}>
        <span>Purchases</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            disabled={isDownloadingPurchases}
            className="btn btn-secondary"
            onClick={downloadPurchases}
            style={{ height: '28px', fontSize: '12px' }}
          >
            {isDownloadingPurchases ? 'Downloading...' : 'Download'}
          </button>
          <button
            type="button"
            disabled={!isSuperAdmin()}
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#addModal"
            onClick={openAddModal}
            style={{ height: '28px', fontSize: '12px' }}
          >
            Add Purchase
          </button>
        </div>
      </div>

      <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
        <AgGridReact
          rowData={purchasesAll}
          columnDefs={purchasesColumnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => String(params.data?.purchaseid ?? params.data?.id)}
          loading={loadingPurchase}
          suppressCellFocus={true}
          context={{ onEdit: openEditModal, onDelete: deletePurchase }}
          domLayout="normal"
        />
      </div>

      {/* ════════════════════════════════════════════════════════════
          Stock & Procurement table
      ════════════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'left', fontWeight: 'bold', padding: '10px', fontSize: '14px' }}>
        Stock&amp;Procurement
      </div>

      <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '12px' }}>
        <input className="form-control" style={{ width: 100 }} placeholder="Fiscal Year" value={stockFilters.FiscalYearOFilter} onChange={setStockFilter('FiscalYearOFilter')} />
        <input className="form-control" style={{ width: 100 }} placeholder="Warehouse" value={stockFilters.WarehouseNameOFilter} onChange={setStockFilter('WarehouseNameOFilter')} />
        <input className="form-control" style={{ width: 150 }} placeholder="Product SAP Code" value={stockFilters.ProductIdOFilter} onChange={setStockFilter('ProductIdOFilter')} />
        <input className="form-control" style={{ width: 120 }} placeholder="Product" value={stockFilters.ProductDescriptionOFilter} onChange={setStockFilter('ProductDescriptionOFilter')} />
        <input className="form-control" style={{ width: 90 }} placeholder="Item Type" value={stockFilters.ItemTypeNameOFilter} onChange={setStockFilter('ItemTypeNameOFilter')} />
        <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={searchStockProcurement}>Search</button>
        <button className="btn btn-secondary" style={{ fontSize: '12px' }} onClick={clearStockFilters}>Clear</button>
      </div>

      <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
        <AgGridReact
          rowData={stockprocurement}
          columnDefs={stockColumnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => String(params.data?.stockviewid ?? params.data?.id)}
          loading={loadingStock}
          suppressCellFocus={true}
          domLayout="normal"
        />
      </div>

      {/* Pagination for Stock */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '20px', fontSize: '12px' }}>
        <button className="btn btn-primary" style={{ height: '28px', fontSize: '12px' }}
          onClick={handlePrevPage} disabled={!stockPrevUrl || loadingStock}>
          Previous
        </button>
        <span>Page {stockPage} of {stockTotalPages}</span>
        <button className="btn btn-primary" style={{ height: '28px', fontSize: '12px' }}
          onClick={handleNextPage} disabled={!stockNextUrl || loadingStock}>
          Next
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════
          Add Purchase Modal
      ════════════════════════════════════════════════════════════ */}
      <Modal id="addModal" title={modalTitle}>
        <FormSelect label="Fiscal Year"    value={form.fyname}           onChange={(e) => setForm((f) => ({ ...f, fyname: e.target.value }))}          options={fiscalYears} />
        <FormSelect label="Warehouse"      value={form.warehousename}    onChange={(e) => setForm((f) => ({ ...f, warehousename: e.target.value }))}    options={warehouseNames} />

        {/* Mode toggle */}
        <div className="input-group mb-3">
          <span className="input-group-text" style={{ fontSize: '12px' }}>Input By</span>
          <div className="form-control d-flex gap-4 align-items-center" style={{ fontSize: '12px' }}>
            <label style={{ cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="radio"
                name="addPurchaseMode"
                value="container"
                checked={addPurchaseMode === 'container'}
                onChange={() => {
                  setAddPurchaseMode('container');
                  const first = containerdescriptions[0] ?? '';
                  setForm((f) => ({ ...f, sapcode: '', itempurchasedname: first }));
                  setPoDelayValue(containerToPoDelay[first] ?? 0);
                }}
                style={{ marginRight: 4 }}
              />
              Container / Pallet
            </label>
            <label style={{ cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="radio"
                name="addPurchaseMode"
                value="sapcode"
                checked={addPurchaseMode === 'sapcode'}
                onChange={() => {
                  setAddPurchaseMode('sapcode');
                  setForm((f) => ({ ...f, itempurchasedname: '', sapcode: '' }));
                  setPoDelayValue(0);
                }}
                style={{ marginRight: 4 }}
              />
              SAP Code
            </label>
          </div>
        </div>

        {addPurchaseMode === 'container' && (
          <>
            <FormSelect
              label="Container/Pallet"
              value={form.itempurchasedname}
              onChange={(e) => {
                const val = e.target.value;
                setForm((f) => ({ ...f, itempurchasedname: val, sapcode: '' }));
                setPoDelayValue(containerToPoDelay[val] ?? 0);
              }}
              options={containerdescriptions}
            />
            <FormReadOnly label="Container PoDelay (months)" value={containerToPoDelay[form.itempurchasedname] ?? ''} />
          </>
        )}

        {addPurchaseMode === 'sapcode' && (
          <>
            <FormSelect
              label="SAP Code"
              value={form.sapcode}
              onChange={(e) => {
                const val = e.target.value;
                setForm((f) => ({ ...f, sapcode: val, itempurchasedname: '' }));
                setPoDelayValue(0);
              }}
              options={sapcodeNames}
            />
            {form.sapcode && sapcodeToDescription[form.sapcode] && (
              <FormReadOnly label="Description" value={sapcodeToDescription[form.sapcode]} />
            )}
          </>
        )}

        <SatelliteCheckboxes value={form.satellite} onChange={(val) => setForm((f) => ({ ...f, satellite: val }))} />
        <FormInput label="Comments" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />

        <PoDelayMonths
          containerPoDelay={isNaN(Number(poDelayValue)) ? 0 : Number(poDelayValue)}
          fyname={Number(form.fyname)}
          onValueChange={setMonthInputs}
          onValueCheckChange={setMonthCheckbox}
          currentMonths={null}
          currentData={null}
          edition={false}
        />

        <button type="button" className="btn btn-primary float-start" style={{ fontSize: '12px' }} onClick={createPurchase}>
          Save
        </button>
      </Modal>

      {/* ════════════════════════════════════════════════════════════
          Edit Purchase Modal
      ════════════════════════════════════════════════════════════ */}
      <Modal id="editModal" title={modalTitle}>
        <FormSelect label="Fiscal Year"   value={form.fyname}           onChange={(e) => setForm((f) => ({ ...f, fyname: e.target.value }))}          options={fiscalYears}     disabled={isEditing} />
        <FormSelect label="Warehouse"     value={form.warehousename}    onChange={(e) => setForm((f) => ({ ...f, warehousename: e.target.value }))}    options={warehouseNames} />
        <FormReadOnly label="Purchase Type"              value={form.purchasetypename} />
        <FormReadOnly label="ContainerID / SAP Code - SKU" value={form.sapcode} />
        <FormReadOnly label="Item Purchased Name"        value={form.itempurchasedname} />
        <SatelliteCheckboxes value={form.satellite} onChange={(val) => setForm((f) => ({ ...f, satellite: val }))} />

        {isContainer && <FormReadOnly label="Container PoDelay (months)" value={containerPoDelay} />}
        {isPallet    && <FormReadOnly label="Pallet PoDelay (months)"    value={palletPoDelay} />}

        <FormInput label="Comments" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />

        <PoDelayMonths
          containerPoDelay={isNaN(Number(containerPoDelay)) ? 0 : Number(containerPoDelay)}
          fyname={Number(form.fyname)}
          onValueChange={setMonthInputs}
          onValueCheckChange={setMonthCheckbox}
          refreshKey={refresh}
          dataFromDb={Object.fromEntries(
            MONTHS.map((m) => {
              const label = m.charAt(0).toUpperCase() + m.slice(1);
              return [label, { value: form[m], check: form[`povalidated${m}`] }];
            })
          )}
          edition={true}
        />

        <button type="button" className="btn btn-primary float-start" style={{ fontSize: '12px' }}
          onClick={form.purchaseid === 0 ? createPurchase : updatePurchase}>
          {form.purchaseid === 0 ? 'Create' : 'Update'}
        </button>
      </Modal>
    </div>
  );
}